import { alpha } from '@mui/material/styles';

// ─────────────────────────────────────────────────────────────────────────────
// BRAND TOKENS
// ─────────────────────────────────────────────────────────────────────────────
export const BRAND = {
  white: '#FFFFFF', // layout background
  charcoal: '#2F2F2F', // nav bars, text, icons
  patina: '#739272', // primary accent
  slate: '#728392', // secondary accent
  sky: '#92a092', // tertiary accent
  mint: '#C9D0C9', // micro-accent
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
function createGradient(color1: string, color2: string) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

interface GradientsPaletteOptions {
  primary: string;
  info: string;
  success: string;
  warning: string;
  error: string;
}

interface ChartPaletteOptions {
  violet: string[];
  blue: string[];
  green: string[];
  yellow: string[];
  red: string[];
}

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
    vibrant?: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
    vibrant?: string;
  }
  interface Palette {
    gradients: GradientsPaletteOptions;
    chart: ChartPaletteOptions;
  }
  interface PaletteOptions {
    gradients: GradientsPaletteOptions;
    chart: ChartPaletteOptions;
  }
}

declare module '@mui/material' {
  interface Color {
    0: string;
    500_8: string;
    500_12: string;
    500_16: string;
    500_24: string;
    500_32: string;
    500_48: string;
    500_56: string;
    500_80: string;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// PALETTE DEFINITIONS
// ─────────────────────────────────────────────────────────────────────────────
// Primary = PATINA
const PRIMARY = {
  lighter: alpha(BRAND.patina, 0.2),
  light: alpha(BRAND.patina, 0.5),
  main: BRAND.patina,
  dark: alpha(BRAND.patina, 0.8),
  darker: alpha(BRAND.patina, 1),
  vibrant: '#579956',
};

// Secondary = SLATE BLUE
const SECONDARY = {
  lighter: alpha(BRAND.slate, 0.2),
  light: alpha(BRAND.slate, 0.5),
  main: BRAND.slate,
  dark: alpha(BRAND.slate, 0.8),
  darker: alpha(BRAND.slate, 1),
  vibrant: 'rgb(70, 90, 105)',
};

// Info = SKY BLUE
const INFO = {
  lighter: alpha(BRAND.sky, 0.2),
  light: alpha(BRAND.sky, 0.5),
  main: BRAND.sky,
  dark: alpha(BRAND.sky, 0.8),
  darker: alpha(BRAND.sky, 1),
};

// Success = MINT
const SUCCESS = {
  lighter: alpha(BRAND.mint, 0.2),
  light: alpha(BRAND.mint, 0.5),
  main: BRAND.mint,
  dark: alpha(BRAND.mint, 0.8),
  darker: alpha(BRAND.mint, 1),
};

// Warning & Error left unchanged for semantic clarity
const WARNING = {
  lighter: '#FFF7CD',
  light: '#ff9800',
  main: '#ed6c02',
  dark: '#593b02',
  darker: '#563801',
};
const ERROR = {
  lighter: '#FFE7D9',
  light: '#ef5350',
  main: '#d32f2f',
  dark: '#c62828',
  darker: '#7A0C2E',
};

// Grey scale with white/charcoal swapped in
const GREY = {
  0: BRAND.white,
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#afafaf',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: BRAND.charcoal,
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8),
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
};

const CHART_COLORS = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4'],
};

const COMMON = {
  common: { black: '#000000', white: BRAND.white },
  primary: { ...PRIMARY, contrastText: BRAND.white },
  secondary: { ...SECONDARY, contrastText: BRAND.white },
  info: { ...INFO, contrastText: BRAND.white },
  success: { ...SUCCESS, contrastText: GREY[800] },
  warning: { ...WARNING, contrastText: GREY[800] },
  error: { ...ERROR, contrastText: BRAND.white },
  contrastThreshold: 4.5,
  grey: GREY,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const palette = {
  light: {
    ...COMMON,
    mode: 'light',
    text: {
      primary: GREY[800],
      secondary: GREY[600],
      disabled: GREY[500],
    },
    background: {
      paper: BRAND.white,
      default: BRAND.white,
      neutral: GREY[200],
    },
    action: {
      active: GREY[600],
      ...COMMON.action,
    },
  },
  dark: {
    ...COMMON,
    mode: 'dark',
    text: {
      primary: BRAND.white,
      secondary: GREY[500],
      disabled: GREY[600],
    },
    background: {
      paper: GREY[800],
      default: GREY[900],
      neutral: GREY[500_16],
    },
    action: {
      active: GREY[500],
      ...COMMON.action,
    },
  },
} as const;

export default palette;
